<script setup lang="ts">import { toRef as _toRef, ref as _ref } from 'vue';

import { isDesktop } from '@consumer/utils/breakpoints'
import type {
  GiftSeriesLocation,
  GreetingCard,
  MultiplaceGift,
  MultiplaceGiftsContentVotes,
  PlaceDetail,
} from '@consumer/types/serializers'

import GiftGuide from '@consumer/modules/multiPlaceGiftIdea/GiftGuide.vue'
import type GiftTemplate from '@consumer/types/GiftTemplate'

import {
  openGiftShowcaseModal, openHowGiftlyWorksModal, openMultiplaceCustomizerModal,
} from '@consumer/services/modals'
import MultiPlaceGiftIdeasApi from '@consumer/api/MultiPlaceGiftIdeasApi'
import { greetingCards } from '@consumer/api'
import { removeBy } from '@shared/array'
import { useSharedData } from '@consumer/composables/sharedData'
import { useGift } from '@consumer/composables/gift'
import { speedbumpText } from '@consumer/constants/disclaimers'
import { faChevronDown } from '@fortawesome/pro-light-svg-icons'
import { useTrustpilot } from '@shared/composables/trustpilot'
import { warnIfContainsCardWord } from '@consumer/utils/compliance'
import newPaperPlaneTiltIcon from '@consumer/assets/icons/new/paper-plane-tilt.svg?svgicon'
import newMapPinIcon from '@consumer/assets/icons/new/map-pin.svg?svgicon'
import newTagLightIcon from '@consumer/assets/icons/new/tag-light.svg?svgicon'
import newPencilIcon from '@consumer/assets/icons/new/pencil.svg?svgicon'
import newPartyHornLightIcon from '@consumer/assets/icons/new/party-horn-light.svg?svgicon'
import v6UserLightIcon from '@consumer/assets/icons/v6/user-light.svg?svgicon'
import previewEnvelopeIcon from '@consumer/assets/icons/preview-envelope.svg?svgicon'
import previewLetterIcon from '@consumer/assets/icons/preview-letter.svg?svgicon'
import previewYesShadowIcon from '@consumer/assets/icons/preview-yes-shadow.svg?svgicon'
import previewGiftlyShadowIcon from '@consumer/assets/icons/preview-giftly-shadow.svg?svgicon'
import previewYesIcon from '@consumer/assets/icons/preview-yes.svg?svgicon'
import newMiscMpStars1Icon from '@consumer/assets/icons/new-misc/mp-stars-1.svg?svgicon'
import newMiscMpStars2Icon from '@consumer/assets/icons/new-misc/mp-stars-2.svg?svgicon'
import newMiscMpStars3Icon from '@consumer/assets/icons/new-misc/mp-stars-3.svg?svgicon'
import NationwidePanel from './NationwidePanel.vue'
import CityPicker from './CityPicker.vue'
import PlaceList from './PlaceList.vue'
import PlaceMap from './PlaceMap.vue'
import { usePrimaryPlace } from './composables'
import Attribution from './Attribution.vue'
import ContentVoting from './ContentVoting.vue'

useTrustpilot()

withDefaults(defineProps<{
  multiPlaceGiftIdea: MultiplaceGift
  gift: GiftTemplate
  themeColor?: string
  logoColor?: string
  isEditing: boolean
  currentVotes: MultiplaceGiftsContentVotes[]
  useWysiwygEditor?: boolean
  canEditMultiplaceGift?: boolean
}>(), { useWysiwygEditor: true,canEditMultiplaceGift: false, })

const emit = defineEmits<{
  startEditingField: []
  finishEditingField: []
}>()

const __$temp_1 = (useSharedData()),
  currentUser = _toRef(__$temp_1, 'currentUser'),
  featureFlags = _toRef(__$temp_1, 'featureFlags');

function previewGiftExperience () {
  // Demo uses generic sample content but shows THIS gift's giftly card.
  if (featureFlags.value.newStaticGreetingCardFlow)
    return openGiftShowcaseModal({ mode: 'demo', gift: previewGift.value })

  return openHowGiftlyWorksModal({ gift: previewGift.value, initialStep: 'preview-landing' })
}

const primaryPlace = (usePrimaryPlace())
const places = computed(() => {
  return [primaryPlace.value].concat(__props.gift.places?.filter(p => p.id !== primaryPlace.value?.id) ?? []).filter(Boolean)
})

const titleContentVoting = computed(() => __props.currentVotes.find(contentVote => contentVote.field === 'title'))
const descriptionContentVoting = computed(() =>
  __props.currentVotes.find(contentVote => contentVote.field === 'description'))

async function titleChanged (title?: string) {
  await warnIfContainsCardWord(title)
  await MultiPlaceGiftIdeasApi.update({ params: __props.multiPlaceGiftIdea, data: { title } })
  emit('finishEditingField')
}

function aiWandClickedTitle () {
  return MultiPlaceGiftIdeasApi.titleRecommendation(__props.multiPlaceGiftIdea)
}

async function aiWandClickedDescription () {
  return MultiPlaceGiftIdeasApi.descriptionRecommendation(__props.multiPlaceGiftIdea)
}

async function descriptionChanged (description?: string) {
  await warnIfContainsCardWord(description)
  return MultiPlaceGiftIdeasApi.update({ params: __props.multiPlaceGiftIdea, data: { description } })
}

let isEditingGiftField = _ref(false)

function startEditingField () {
  isEditingGiftField.value = true
  emit('startEditingField')
}

function finishEditingField () {
  isEditingGiftField.value = false
  emit('finishEditingField')
}

async function vote (field: string, vote: string) {
  const data = { field, vote }
  const { contentVote } = await MultiPlaceGiftIdeasApi.vote({ params: __props.multiPlaceGiftIdea, data })
  __props.currentVotes.push(contentVote)
}

async function unvote (field: string) {
  await MultiPlaceGiftIdeasApi.unvote({ params: __props.multiPlaceGiftIdea, data: { field } })
  removeBy(__props.currentVotes, contentVote => contentVote.field === field)
}

const hasCityTags = computed(() => cityTags.value.length > 0 || __props.multiPlaceGiftIdea.giftSeries != null)
const showAllLocations = ref(false)

const cityTags = computed(() => {
  if (__props.multiPlaceGiftIdea.featuredCity != null) {
    return [`${__props.multiPlaceGiftIdea.featuredCity.name}, ${__props.multiPlaceGiftIdea.featuredCity.stateCode}`]
  }

  if (!__props.multiPlaceGiftIdea.places || __props.multiPlaceGiftIdea.places.length === 0) {
    return []
  }

  const places = __props.multiPlaceGiftIdea.places
    .filter(place => place.city && place.state)
    .map(place => `${place.city}, ${place.state}`)

  const fequenciesMap = new Map()
  for (const place of places) {
    if (!fequenciesMap.has(place)) fequenciesMap.set(place, 1)
    else fequenciesMap.set(place, fequenciesMap.get(place) + 1)
  }

  const includeOnlineTag = __props.multiPlaceGiftIdea.places.some(place =>
    place.categories?.map(category => category.toLowerCase())?.includes('online'))
  const includeNationalTag = __props.multiPlaceGiftIdea.places.every(
    place => place.isNationwide || place.categories
      ?.map(category => category.toLowerCase())
      ?.includes('online'))

  let tags: string[] = []
  if (includeNationalTag) {
    tags.push('Nationwide')
  }

  if (includeOnlineTag) {
    tags.push('Online')
  }

  const frequenciesArr = Array.from(fequenciesMap.entries()).sort((a, b) => b[1] - a[1])
  if (showAllLocations.value) {
    tags = tags.concat(frequenciesArr.map(arr => arr[0]))
  }
  else {
    if (tags.length + frequenciesArr.length <= 4) {
      tags = tags.concat(frequenciesArr.map(arr => arr[0]))
    }
    else {
      const locationsToShow = 3 - tags.length
      const locations = frequenciesArr.length - locationsToShow === 1 ? 'location' : 'locations'
      tags = tags.concat([...frequenciesArr.slice(0, locationsToShow)
        .map(arr => arr[0]), `+${frequenciesArr.length - locationsToShow} other ${locations}`])
    }
  }

  return tags
})

const previewGift: ComputedRef<GiftTemplate> = computed(() => {
  return {
    giftlyVersion: 3,
    greeting: {
      from: 'Me',
      to: 'You',
      message: 'Thank you for everything! I hope you have a great day. Looking forward to seeing you soon.',
      ...__props.gift.greeting,
    },
    ...__props.gift,
    giftlyMetadata: {
      tagline: 'Any Restaurant',
      ...__props.gift.giftlyMetadata,
      themeColor: __props.themeColor ?? __props.gift.giftlyMetadata?.themeColor,
    },
  } as GiftTemplate
})

const {
  primaryBgColor,
  complementaryBgColor,
} = useGift(previewGift as any)

// Face of the greeting card the demo actually shows (the showcase card), so the
// preview-experience collage matches the walkthrough it opens. Only the new
// static flow's demo uses it; the legacy modal keeps the stylized fallback.
const showcaseCardImageUrl = ref<string | null>(null)
onMounted(async () => {
  if (!featureFlags.value?.newStaticGreetingCardFlow) return
  try {
    const card = await greetingCards.showcaseCard<GreetingCard>()
    showcaseCardImageUrl.value = card.previewImageUrl
  }
  catch (e) {
    // Keep the stylized fallback graphic on failure.
    console.warn('Failed to load showcase card for preview collage:', e)
  }
})
function getTagString (tagType: string, lowercase: boolean) {
  const tags = __props.multiPlaceGiftIdea.giftTags?.filter(tag => tag.tagType === tagType)
  const tagStr = tags?.map(tag => tag.tag).join(', ')
  return lowercase ? tagStr?.toLowerCase() : tagStr
}

const interestTags = computed(() => getTagString('interest', true))
const hasInterestTags = computed(() => !!interestTags.value)

const occasionTags = computed(() => getTagString('occasion', false))
const hasOccasionTags = computed(() => !!occasionTags.value)

const recipientTags = computed(() => getTagString('recipient', false))
const hasRecipientTags = computed(() => !!recipientTags.value)

function editTagsClicked () {
  openMultiplaceCustomizerModal({
    gift: __props.gift,
    multiplaceGift: __props.multiPlaceGiftIdea,
    createOrEditMultiplace: true,
    initialStep: 'Tags',
  })
}

function placeListClicked (place: PlaceDetail) {
  const el = document.querySelector(`[data-placeid="${place.giftlyId}"]`)
  el?.scrollIntoView({ behavior: 'smooth' })
}

function hasCoordinates (place: PlaceDetail) {
  if (place.lat == null || place.lon == null) {
    return false
  }

  return place.lat !== '0.0' || place.lon !== '0.0'
}

const showMap = computed(() => __props.multiPlaceGiftIdea.places?.some(place => !place.isNationwide && hasCoordinates(place)))

const showNationwidePanel = computed(() => __props.multiPlaceGiftIdea.places?.every(place => place.isNationwide))

const otherMultiplaceGiftsInSeries = ref<GiftSeriesLocation[]>([])

onMounted(async () => {
  function handleLinkClick (event: Event) {
    // eslint-disable-next-line no-alert
    if (confirm(speedbumpText)) {
      window.open((event.target as HTMLAnchorElement).href, '_blank')
    }
    event.preventDefault()
  }

  document.querySelectorAll('.description-section a, .gift-guide-description a').forEach((el) => {
    el.addEventListener('click', handleLinkClick)
  })

  if (__props.multiPlaceGiftIdea.giftSeries && __props.multiPlaceGiftIdea.published) {
    otherMultiplaceGiftsInSeries.value = await MultiPlaceGiftIdeasApi.giftSeries({ params: __props.multiPlaceGiftIdea })
  }
})

watch(() => __props.multiPlaceGiftIdea, async () => {
  if (__props.multiPlaceGiftIdea.giftSeries && __props.multiPlaceGiftIdea.published) {
    otherMultiplaceGiftsInSeries.value = await MultiPlaceGiftIdeasApi.giftSeries({ params: __props.multiPlaceGiftIdea })
  }
  else {
    otherMultiplaceGiftsInSeries.value = []
  }
})

const seriesCTA = computed(() => {
  if (otherMultiplaceGiftsInSeries.value.length > 0) {
    const containsNationwideGift = otherMultiplaceGiftsInSeries.value.some(
      giftSeriesLocation => giftSeriesLocation.nationwideGift)
    const count = otherMultiplaceGiftsInSeries.value.length
    if (containsNationwideGift) {
      if (count === 0) {
        return 'Available nationwide'
      }
      else {
        return `Available in ${count - 1} other cities & nationwide`
      }
    }
    else {
      return `Available in ${count} other cities`
    }
  }

  return ''
})

</script>

<template>
  <div id="mp-gift-guide-content">
    <div
      class="px-6 lg:px-10 py-6 lg:py-12 mx-auto max-w-md lg:max-w-screen-lg xl:max-w-screen-lg flex gap-15"
    >
      <div class="w-full lg:w-500px space-y-3 lg:space-y-6">
        <div class="space-y-3 lg:space-y-6">
          <div
            v-if="hasCityTags"
            class="flex overflow-x-scroll hide-scrollbar lg:overflow-x-auto lg:flex-wrap gap-2"
            data-testid="city-tags"
          >
            <div
              v-for="cityTag in cityTags"
              :key="cityTag"
              class="text-sm text-grey-500 font-semibold bg-grey-300 p-2 rounded-md whitespace-nowrap"
              data-testid="city-tag"
              :class="cityTag.indexOf('+') === 0 ? 'clickable' : ''"
              @click="cityTag.indexOf('+') === 0 ? showAllLocations = true : ''"
            >
              {{ cityTag }}
            </div>
            <VDropdown v-if="otherMultiplaceGiftsInSeries.length > 0" placement="bottom" :flip="false">
              <div
                class="text-sm flex flex-row px-3 py-2 gap-2
                justify-center items-center rounded-md bg-surface-blue border-grey-primary
                text-v2-active clickable font-weight-600 border-1 city-picker"
              >
                <div>{{ seriesCTA }}</div>
                <GIcon :icon="faChevronDown"/>
              </div>
              <template #popper>
                <CityPicker :otherMultiplaceGiftsInSeries="otherMultiplaceGiftsInSeries"/>
              </template>
            </VDropdown>
          </div>
          <h1 class="text-2xl lg:text-3xl" data-testid="title-section">
            <template v-if="!isEditing && currentUser?.canVoteOnContent">
              <ContentVoting
                :contentVote="titleContentVoting"
                @thumbsUpVote="vote('title', 'thumbs_up')"
                @thumbsDownVote="vote('title', 'thumbs_down')"
                @voteRemoved="unvote('title')"
              >
                <template #content>
                  <span data-testid="title" v-html="multiPlaceGiftIdea.title"/>
                </template>
              </ContentVoting>
            </template>
            <GEditableText
              v-else-if="isEditing"
              v-model="multiPlaceGiftIdea.title"
              :aiOptionCallback="aiWandClickedTitle"
              :disabled="isEditingGiftField"
              :onSaveChanges="titleChanged"
              @startEditing="startEditingField"
              @finishEditing="finishEditingField"
            />
            <span
              v-else
              data-testid="title"
              v-html="multiPlaceGiftIdea.title"
            />
          </h1>

          <div class="text-xl allow-list-items description-section" data-testid="description-section">
            <template v-if="!isEditing && currentUser?.canVoteOnContent">
              <ContentVoting
                :contentVote="descriptionContentVoting"
                @thumbsUpVote="vote('description', 'thumbs_up')"
                @thumbsDownVote="vote('description', 'thumbs_down')"
                @voteRemoved="unvote('description')"
              >
                <template #content>
                  <span data-testid="description" v-html="multiPlaceGiftIdea.description"/>
                </template>
              </ContentVoting>
            </template>
            <GEditableText
              v-else-if="isEditing"
              v-model="multiPlaceGiftIdea.description"
              :multiline="true"
              :aiOptionCallback="aiWandClickedDescription"
              :disabled="isEditingGiftField"
              :useWysiwygEditor="useWysiwygEditor"
              :onSaveChanges="descriptionChanged"
              @startEditing="startEditingField"
              @finishEditing="finishEditingField"
            />
            <span v-else data-testid="description" v-html="multiPlaceGiftIdea.description"/>
          </div>
        </div>

        <div v-if="showMap" class="rounded-v2lg bg-surface-yellow">
          <div
            class="text-center text-lg font-semibold py-4"
          >
            Suggested for use at these {{ multiPlaceGiftIdea.places?.length }} places
          </div>
          <PlaceMap
            :places="multiPlaceGiftIdea.places || []"
          />
        </div>

        <NationwidePanel
          v-if="showNationwidePanel"
          :multiplaceGift="multiPlaceGiftIdea"
        />
        <PlaceList
          :places="multiPlaceGiftIdea.places || []"
          @click="placeListClicked"
        />

        <Attribution
          class="py-5 border-y-1 border-grey-primary"
          :multiplaceGift="multiPlaceGiftIdea"
          :isEditing="isEditing"
          :canEditMultiplaceGift="canEditMultiplaceGift"
        />

        <div>
          <div class="font-semibold text-xs mb-3">
            Overview
          </div>

          <div class="text-sm space-y-3">
            <div class="flex items-center gap-1">
              <GIcon :svg="newPaperPlaneTiltIcon"/>
              <span>send via text, email &amp; more</span>
            </div>

            <div
              class="flex items-center gap-1"
            >
              <GIcon :svg="newMapPinIcon"/>
              <span>{{ gift.places?.length ?? 1 }} suggested merchants</span>
            </div>

            <div
              v-if="hasInterestTags || isEditing"
              class="flex flex-row items-center gap-1"
            >
              <GIcon :svg="newTagLightIcon"/>
              <span v-if="hasInterestTags" data-testid="interest-tags">
                good for people who like
                {{ interestTags }}
              </span>
              <GIcon
                v-if="isEditing"
                :svg="newPencilIcon"
                class="clickable"
                data-testid="edit-interest-tags-button"
                @click="editTagsClicked"
              />
            </div>

            <div
              v-if="hasOccasionTags || isEditing"
              class="flex flex-row items-center gap-1"
            >
              <GIcon :svg="newPartyHornLightIcon"/>
              <span v-if="hasOccasionTags" data-testid="occasion-tags">
                recommended for
                {{ occasionTags }}
              </span>
              <GIcon
                v-if="isEditing"
                :svg="newPencilIcon"
                class="clickable"
                data-testid="edit-occasion-tags-button"
                @click="editTagsClicked"
              />
            </div>

            <div
              v-if="hasRecipientTags || isEditing"
              class="flex flex-row items-center gap-1"
            >
              <GIcon :svg="v6UserLightIcon"/>
              <span v-if="hasRecipientTags" data-testid="recipient-tags">
                most relavent for
                {{ recipientTags }}
              </span>
              <GIcon
                v-if="isEditing"
                :svg="newPencilIcon"
                class="clickable"
                data-testid="edit-recipient-tags-button"
                @click="editTagsClicked"
              />
            </div>
          </div>
        </div>

        <div
          class="flex flex-col justify-center items-center pb-16 lg:pb-20 relative cursor-pointer group"
          @click="previewGiftExperience"
        >
          <div class="relative lg:top-5" style=" height: 204px;width: 280px;">
            <GIcon
              :svg="previewEnvelopeIcon"
              class="absolute top-0 left-0"
              :style="`
                color: ${primaryBgColor};
                width: 160px;
                height: 174px;
                stroke:none;`"
            />
            <GIcon
              :svg="previewLetterIcon"
              class="absolute"
              :style="`
                color: ${primaryBgColor};
                width: 145px;
                height: 166px;
                top: 20px;
                left:57px;
                stroke:none;`"
            />
            <GIcon
              :svg="previewYesShadowIcon"
              class="absolute"
              style="height: 37px;left: 160px;top: 143px;width: 171px;"
            />
            <GIcon
              :svg="previewGiftlyShadowIcon"
              class="absolute"
              style=" height: 34px; left: 80px;top: 168px; width: 187px;"
            />
            <img
              v-if="showcaseCardImageUrl"
              :src="showcaseCardImageUrl"
              alt=""
              class="absolute rounded-v2sm object-cover"
              style=" height: 176px; left: 145px; top: 5px;width: 133px;"
            >
            <GIcon
              v-else
              :svg="previewYesIcon"
              class="absolute"
              :style="`
                color: ${complementaryBgColor};
                width: 133px;
                height: 176px;
                top: 5px;
                left:145px;
                stroke:none;`"
            />
            <div
              class="absolute"
              style=" height: 98px; left: 70px; top: 100px;width: 147px;"
            >
              <GiftlyV3Image
                :gift="gift"
                :inCustomizer="true"
              />
            </div>
          </div>

          <div
            class="w-full flex flex-col items-center absolute bottom-0 bg-white -z-1 rounded-v2lg shadow-v2lg"
            :style="{
              height: isDesktop ? '133px' : '200px',
              paddingTop: isDesktop ? '90px' : '150px',
            }"
          >
            <a class="font-semibold text-primary-base group-hover:underline underline-offset-4">
              Preview gift experience
            </a>
          </div>
        </div>

        <div
          class="w-full bg-white flex items-center justify-center gap-4 lg:gap-6 py-6
            text-base lg:text-2xl rounded-v2lg shadow-v2lg font-semibold"
        >
          <div>
            <GIcon :svg="newMiscMpStars1Icon" :size="isDesktop ? '32px' : '24px'"/>
          </div>
          <div>
            Satisfaction<br>
            Guaranteed
          </div>
          <div>
            <GIcon :svg="newMiscMpStars2Icon" :size="isDesktop ? '32px' : '24px'"/>
          </div>

        </div>

        <div class="bg-surface-yellow allow-list-items">
          <div class="font-semibold p-6 text-center">
            Your gift comes with below guidebook of suggestions
          </div>
          <GiftGuide
            ref="giftGuide"
            :gift="gift"
            :places="places"
            :themeColor="themeColor"
            :logoColor="logoColor"
            :isEditing="isEditing"
            :multiPlaceGiftIdea="multiPlaceGiftIdea"
            :disableEditing="isEditingGiftField"
            :editGiftTips="isEditing"
            :currentVotes="currentVotes"
            :useWysiwygEditor="useWysiwygEditor"
            @startEditingField="startEditingField"
            @finishEditingField="finishEditingField"
            @thumbsUpVote="vote('gift_guide_blurb', 'thumbs_up')"
            @thumbsDownVote="vote('gift_guide_blurb', 'thumbs_down')"
            @unvote="unvote('gift_guide_blurb')"
          />
        </div>

        <div class="pt-6">
          <div
            class="w-full bg-white flex flex-col items-center justify-center gap-2 py-6
              text-lg lg:text-2xl font-semibold relative rounded-v2lg shadow-v2lg
            "
          >
            <div class="absolute -top-6 left-1/2 transform -translate-x-1/2">
              <GIcon :svg="newMiscMpStars3Icon" size="64px"/>
            </div>
            <div class="mt-6">
              Millions of gifts sent with Giftly
            </div>
            <div
              class="trustpilot-widget pad-bottom-10"
              data-locale="en-US"
              data-template-id="5419b732fbfb950b10de65e5"
              data-businessunit-id="5aa773d465a75300015d8d3b"
              data-style-height="24px"
              data-style-width="100%"
              data-theme="light"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="css">
.hide-scrollbar {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none;  /* Firefox */

  &::-webkit-scrollbar {
    display: none;
  }
}

.allow-list-items {
  ul {
    list-style: inside;
  }
}
</style>
